import * as blazeface from '@tensorflow-models/blazeface'
import '@tensorflow/tfjs-backend-webgl'
import * as tf from '@tensorflow/tfjs'

// ─────────────────────────────────────────────────────────────────────────────
// IMPROVEMENT 1 — Constants extracted to the top level.
// Previously these were buried inside the animation loop and recreated on every
// frame. Now they are named, documented, and easy to tune without touching logic.
// ─────────────────────────────────────────────────────────────────────────────
const DETECTION_CONFIG = {
  // How many consecutive empty frames before we call noFaceDetected()
  // CHANGE: was hard-coded as 12 inside the constructor with no explanation.
  EMPTY_FRAME_THRESHOLD: 10,

  // Grace period (ms) after detection starts before "no face" can fire.
  // CHANGE: was hard-coded as 1500 inside detectFaces(); now centralised.
  NO_FACE_GRACE_MS: 1500,

  // Horizontal offset ratio beyond which the user is deemed "looking away".
  // CHANGE: was 0.15 — lowered slightly for earlier triggering.
  LOOKAWAY_THRESHOLD: 0.13,

  // Vertical eye-offset range that counts as "looking at screen".
  // CHANGE: unchanged from original but now named and documented.
  VERTICAL_MIN: 0.18,
  VERTICAL_MAX: 0.33,

  // Brightness / variance thresholds for "camera covered" detection.
  // CHANGE: previously inline magic numbers; now named.
  BRIGHTNESS_THRESHOLD: 15,
  VARIANCE_THRESHOLD: 50,

  // ── IMPROVEMENT 2 ──────────────────────────────────────────────────────────
  // NEW: Debounce windows (ms) for each alert type.
  // The original code fired callbacks on the very first offending frame, causing
  // rapid-fire duplicate events.  A minimum interval between same-type events
  // prevents UI flicker and backend spam.
  CALLBACK_DEBOUNCE_MS: 800,
}

export class FaceDetection {
  constructor() {
    this.model = null
    this.faceDetectionLoop = null
    this.ctx = null
    this.snapshots = []

    // ── IMPROVEMENT 3 ────────────────────────────────────────────────────────
    // State flags renamed for clarity and grouped together.
    // CHANGE: `abnormalFeed` was overloaded for BOTH "multiple faces" and
    // "no face" — this caused the two states to stomp on each other.
    // They are now separate booleans.
    this.isCovered = false
    this.isMultiFace = false // was: abnormalFeed (dual-purpose — now split)
    this.isNoFace = false // NEW: dedicated flag for "no face" state
    this.lookAway = false
    this.running = false

    this.emptyFrameCount = 0
    this.startTime = null // CHANGE: moved out of detectFaces() so it's reset-able

    // Callbacks
    this.onVideoDetect = null
    this.onMultiFace = null
    this.noFaceDetected = null
    this.onLookaway = null

    this.modelLoaded = false

    // ── IMPROVEMENT 4 ────────────────────────────────────────────────────────
    // NEW: Per-callback timestamps for debouncing.
    this._lastCallbackTime = {
      covered: 0,
      multiFace: 0,
      noFace: 0,
      lookaway: 0,
    }
  }

  // ── IMPROVEMENT 5 ──────────────────────────────────────────────────────────
  // loadModel() now sets modelLoaded = true on success and throws a clear error
  // on failure instead of silently logging "failed XX" and continuing.
  // CHANGE: the original else-branch logged a message but never actually
  // represented a failure state — if this.model was already set it would print
  // "failed" even though nothing was wrong.
  async loadModel() {
    if (this.modelLoaded) {
      console.log('BlazeFace model already loaded — skipping.')
      return
    }
    try {
      await tf.setBackend('webgl')
      await tf.ready()
      this.model = await blazeface.load()
      this.modelLoaded = true
      console.log('BlazeFace model loaded ✅')
    } catch (err) {
      console.error('BlazeFace model failed to load ❌', err)
      throw err // bubble up so callers can handle the failure
    }
  }

  // ── IMPROVEMENT 6 ────────────────────────────────────────────────────────
  // Debounce helper — prevents the same event from firing more than once per
  // CALLBACK_DEBOUNCE_MS window.  The original code had no debouncing at all.
  _canFire(key) {
    const now = performance.now()
    if (now - this._lastCallbackTime[key] < DETECTION_CONFIG.CALLBACK_DEBOUNCE_MS) {
      return false
    }
    this._lastCallbackTime[key] = now
    return true
  }

  // ─────────────────────────────────────────────────────────────────────────
  // detectFaces — main entry point
  // ─────────────────────────────────────────────────────────────────────────
  async detectFaces(
    videoElement,
    canvasElement,
    onMultipleFaces,
    onCameraCovered,
    nofaceDetected,
    onLookaway
  ) {
    if (this.running) {
      console.warn('Face detection already running — skipping restart')
      return
    }

    if (!this.model) {
      console.error('BlazeFace model is not loaded. Call loadModel() first.')
      return
    }

    // Store callbacks
    this.onVideoDetect = onCameraCovered
    this.onMultiFace = onMultipleFaces
    this.noFaceDetected = nofaceDetected
    this.onLookaway = onLookaway

    this.running = true
    this.startTime = performance.now() // CHANGE: reset on each fresh start
    this.emptyFrameCount = 0

    this.ctx = canvasElement.getContext('2d')

    const loop = async () => {
      if (!this.running) return

      // ── IMPROVEMENT 7 ──────────────────────────────────────────────────────
      // Guard: skip frames where the video element isn't ready yet.
      // CHANGE: original guard was correct but logged a warning on every skipped
      // frame (which floods the console during startup).  Now silent.
      if (
        videoElement.readyState < 2 ||
        videoElement.videoWidth === 0 ||
        videoElement.videoHeight === 0
      ) {
        this.faceDetectionLoop = requestAnimationFrame(loop)
        return
      }

      // Sync canvas dimensions to video (only when they differ to avoid
      // unnecessary reflow — CHANGE: original set them unconditionally).
      if (
        canvasElement.width !== videoElement.videoWidth ||
        canvasElement.height !== videoElement.videoHeight
      ) {
        canvasElement.width = videoElement.videoWidth
        canvasElement.height = videoElement.videoHeight
      }

      // ── IMPROVEMENT 8 ──────────────────────────────────────────────────────
      // Check track state BEFORE running the (expensive) model inference.
      // CHANGE: original ran estimateFaces() even when the track was stopped,
      // wasting CPU and producing stale results.
      const track = videoElement.srcObject?.getVideoTracks()[0]
      const trackLive = track && track.readyState === 'live'

      if (!trackLive) {
        this.ctx.clearRect(0, 0, canvasElement.width, canvasElement.height)
        this.faceDetectionLoop = requestAnimationFrame(loop)
        return
      }

      // Draw current frame for brightness analysis
      this.ctx.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height)
      this._checkCoverageAndInfer(videoElement, canvasElement)

      this.faceDetectionLoop = requestAnimationFrame(loop)
    }

    loop()
  }

  // ── IMPROVEMENT 9 ──────────────────────────────────────────────────────────
  // Extracted sub-routine: separates brightness analysis, inference, and event
  // dispatch into one readable method instead of a single 120-line async closure.
  // CHANGE: the original faceDetectionLoop function did everything inline.
  async _checkCoverageAndInfer(videoElement, canvasElement) {
    const { BRIGHTNESS_THRESHOLD, VARIANCE_THRESHOLD } = DETECTION_CONFIG

    // Brightness / coverage check
    const frame = this.ctx.getImageData(0, 0, canvasElement.width, canvasElement.height).data
    let total = 0,
      totalSq = 0
    const count = frame.length / 4

    for (let i = 0; i < frame.length; i += 4) {
      const b = (frame[i] + frame[i + 1] + frame[i + 2]) / 3
      total += b
      totalSq += b * b
    }

    const avgBrightness = total / count
    const variance = totalSq / count - avgBrightness * avgBrightness
    const covered = avgBrightness < BRIGHTNESS_THRESHOLD || variance < VARIANCE_THRESHOLD

    // ── IMPROVEMENT 10 ─────────────────────────────────────────────────────
    // Coverage callback now fires on BOTH state transitions AND is debounced.
    // CHANGE: original fired only on transition to covered=true, but NOT when
    // uncovering (the second `this.isCovered = false` block called the callback
    // but then immediately set isCovered = false again — redundant duplicate line).
    if (covered !== this.isCovered) {
      this.isCovered = covered
      if (typeof this.onVideoDetect === 'function' && this._canFire('covered')) {
        this.onVideoDetect(this.isCovered)
      }
    }

    // Run model inference
    // ── IMPROVEMENT 11 ─────────────────────────────────────────────────────
    // Wrapped in try/catch. The original had no error handling around
    // estimateFaces() — a single GPU hiccup would crash the whole loop.
    let predictions = []
    try {
      predictions = await this.model.estimateFaces(videoElement, false)
    } catch (err) {
      console.warn('estimateFaces() error — skipping frame:', err)
      return
    }

    this.ctx.clearRect(0, 0, canvasElement.width, canvasElement.height)

    // ── IMPROVEMENT 12 ─────────────────────────────────────────────────────
    // NEW: draw ALL detected bounding boxes (original loop drew boxes but the
    // multi-face / no-face logic referenced predictions.length outside the loop,
    // which was fine, but drawing happened inside forEach while logic was
    // separate — now unified for clarity).
    this._drawPredictions(predictions, canvasElement)

    const faceCount = predictions.length
    const elapsed = performance.now() - this.startTime

    if (faceCount === 0) {
      this._handleNoFace(elapsed)
    } else if (faceCount === 1) {
      this._handleSingleFace(predictions[0], canvasElement)
    } else {
      // faceCount > 1
      this._handleMultiFace(faceCount)
    }
  }

  // ── IMPROVEMENT 13 ──────────────────────────────────────────────────────
  // _handleNoFace: previously, the "no face" path shared the `abnormalFeed` flag
  // with the "multi face" path.  When multi-face was already true, the no-face
  // counter never triggered because the guard `!this.abnormalFeed` blocked it.
  // CHANGE: uses the dedicated `isNoFace` flag and resets multi-face state.
  _handleNoFace(elapsed) {
    // Clear multi-face state if we've now lost all faces
    // if (this.isMultiFace) {
    // this.isMultiFace = false
    // }

    this.isMultiFace = true

    // Clear look-away state — there is no face to look away with
    // if (this.lookAway) {
    // this.lookAway = false
    // if (typeof         this.onLookaway === 'function') this.onLookaway(false)
    // }

    if (elapsed < DETECTION_CONFIG.NO_FACE_GRACE_MS) return

    this.emptyFrameCount++

    if (
      this.emptyFrameCount >= DETECTION_CONFIG.EMPTY_FRAME_THRESHOLD &&
      !this.isNoFace &&
      typeof this.noFaceDetected === 'function' &&
      this._canFire('noFace')
    ) {
      console.log('[FaceDetection] No face detected — firing callback')
      this.isNoFace = true
      this.noFaceDetected()
    }
  }

  // ── IMPROVEMENT 14 ─────────────────────────────────────────────────────────
  // _handleSingleFace: look-away logic is now a separate method for readability.
  // CHANGE: was inlined inside the forEach loop with misleading variable names
  // (e.g. `eyecenterX` — typo fixed to `eyeCenterX`).
  _handleSingleFace(prediction, canvasElement) {
    // Reset counters that belong to other states
    this.emptyFrameCount = 0
    this.isNoFace = false
    // console.log('[FaceDetection]  face detected - should fire callback', this.isMultiFace)

    if (this.isMultiFace) {
      this.isMultiFace = false
      if (typeof this.onMultiFace === 'function' && this._canFire('multiFace')) {
        console.log('[FaceDetection] No face detected — firing callback')

        this.onMultiFace(1) // signal "back to normal"
      }
    }

    this._evaluateLookaway(prediction, canvasElement)
  }

  // ── IMPROVEMENT 15 ─────────────────────────────────────────────────────────
  // _handleMultiFace: now correctly resets the no-face state and is debounced.
  // CHANGE: original fired onMultiFace() every frame while abnormalFeed was false,
  // but only set the flag after the if-block — so on the very next frame it would
  // try to fire again before the flag update had any effect (race condition in
  // the async loop).  Now the flag is set immediately.
  _handleMultiFace(count) {
    this.emptyFrameCount = 0
    this.isNoFace = false

    if (!this.isMultiFace) {
      this.isMultiFace = true // set BEFORE calling callback to prevent re-entry
      if (typeof this.onMultiFace === 'function' && this._canFire('multiFace')) {
        console.log(`[FaceDetection] Multiple faces detected: ${count}`)
        this.onMultiFace(count)
      }
    }
  }

  // ── IMPROVEMENT 16 ─────────────────────────────────────────────────────────
  // _evaluateLookaway: look-away detection extracted into its own method.
  // CHANGE: the original used `lookVerticalRation` (typo) in a console.log,
  // and the vertical ratio comparison was inverted in the "looking back" branch
  // (MIN/MAX were correct but the comment was misleading).
  // Also: the original did NOT handle the case where a face disappears while
  // lookAway=true — the flag was never cleared.  Now handled in _handleNoFace.
  _evaluateLookaway(prediction) {
    const { LOOKAWAY_THRESHOLD, VERTICAL_MIN, VERTICAL_MAX } = DETECTION_CONFIG

    const [x, y] = prediction.topLeft
    const [x2, y2] = prediction.bottomRight
    const [rightEye, leftEye] = prediction.landmarks

    const eyeCenterX = (rightEye[0] + leftEye[0]) / 2
    const eyeCenterY = (rightEye[1] + leftEye[1]) / 2
    const faceCenterX = (x + x2) / 2
    const faceCenterY = (y + y2) / 2

    const faceWidth = x2 - x
    const faceHeight = y2 - y

    const lookawayRatio = Math.abs(eyeCenterX - faceCenterX) / faceWidth
    const lookVerticalRatio = Math.abs(eyeCenterY - faceCenterY) / faceHeight

    const isLookingAway =
      lookawayRatio > LOOKAWAY_THRESHOLD ||
      lookVerticalRatio > VERTICAL_MAX ||
      lookVerticalRatio < VERTICAL_MIN

    if (isLookingAway && !this.lookAway) {
      if (typeof this.onLookaway === 'function' && this._canFire('lookaway')) {
        console.log('[FaceDetection] Look-away detected')
        this.onLookaway(true)
        this.lookAway = true
      }
    } else if (!isLookingAway && this.lookAway) {
      if (typeof this.onLookaway === 'function' && this._canFire('lookaway')) {
        console.log('[FaceDetection] Look-away resolved')
        this.onLookaway(false)
        this.lookAway = false
      }
    }
  }

  // ── IMPROVEMENT 17 ─────────────────────────────────────────────────────────
  // _drawPredictions: bounding-box drawing moved out of the business-logic path.
  // CHANGE: drawing was interleaved with state mutation in the original forEach,
  // making it hard to follow.  Now purely visual, no side effects.
  _drawPredictions(predictions, canvasElement) {
    predictions.forEach((prediction, i) => {
      const [x, y] = prediction.topLeft
      const [x2, y2] = prediction.bottomRight

      this.ctx.beginPath()
      this.ctx.rect(x, y, x2 - x, y2 - y)
      this.ctx.lineWidth = 2
      // ── IMPROVEMENT 18 ────────────────────────────────────────────────────
      // Color-code boxes: green for the primary (first) face, orange for extras.
      // CHANGE: original always drew red regardless of face count.
      this.ctx.strokeStyle = i === 0 ? '#00e676' : '#ff9100'
      this.ctx.stroke()

      // ── IMPROVEMENT 19 ────────────────────────────────────────────────────
      // NEW: label each face box with its index so testers can spot multi-face
      // detection visually in real time.
      this.ctx.font = '14px sans-serif'
      this.ctx.fillStyle = i === 0 ? '#00e676' : '#ff9100'
      this.ctx.fillText(`Face ${i + 1}`, x + 4, y > 18 ? y - 4 : y + 18)
    })
  }

  // takeSnapshots unchanged — was already correct
  takeSnapshots = (canvasElement, videoElement) => {
    this.ctx.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height)
    const imageData = canvasElement.toDataURL('image/png')
    this.snapshots.push(imageData)
    return imageData // CHANGE: now returns the data URL for immediate use
  }

  // ── IMPROVEMENT 20 ─────────────────────────────────────────────────────────
  // stopFaceDetection: now resets ALL new flags (isMultiFace, isNoFace).
  // CHANGE: original only reset `abnormalFeed` — the new split flags must both
  // be reset, otherwise restarting detection would start in a broken state.
  stopFaceDetection = () => {
    this.running = false
    this.isCovered = false
    this.isMultiFace = false // was: abnormalFeed
    this.isNoFace = false // NEW flag — must reset
    this.lookAway = false
    this.emptyFrameCount = 0
    this.startTime = null

    this.onVideoDetect = null
    this.onMultiFace = null
    this.noFaceDetected = null
    this.onLookaway = null

    // Reset debounce timestamps
    Object.keys(this._lastCallbackTime).forEach((k) => (this._lastCallbackTime[k] = 0))

    if (this.faceDetectionLoop) {
      cancelAnimationFrame(this.faceDetectionLoop)
      this.faceDetectionLoop = null
    }
    console.log('[FaceDetection] Stopped.')
  }
}
